import type { ClientTeamJSON } from "work/work-agreements/teams/client-team/_schema/client-team-schema";

/**
 * One client-team membership on the wire: the two GUIDs the
 * `PUT .../client-team-members` command carries, and nothing else.
 */
export type ProposalClientTeamMemberReference = {
  userId: string;
  legalEntityId: string;
};

/**
 * A client-team roster the viewer has edited but not yet saved, together with the authoritative
 * roster it was edited against.
 *
 * Client-team membership is reference state written by its own command, so it is not a content
 * operation and never appears in the negotiation's operation queue. It still waits for the same
 * explicit Save the rest of the editor waits for (#5116), which is what this draft holds.
 *
 * `baseline` is the reason a teammate's roster change cannot be silently overwritten: the command
 * replaces the whole roster, so without a merge base a stale staged list would remove whoever
 * somebody else has since added. See {@link rebaseProposalClientTeamRoster}.
 */
export type ProposalClientTeamRosterDraft = {
  members: ProposalClientTeamMemberReference[];
  baseline: ProposalClientTeamMemberReference[];
};

/** A membership's identity — the compound pair the roster table and the server both key on. */
export function proposalClientTeamMemberKey(member: ProposalClientTeamMemberReference): string {
  return `${member.userId}:${member.legalEntityId}`;
}

/**
 * The roster the server currently holds for a proposal, leader excluded.
 *
 * The Client Representative leads the roster table but is not a member of the list the replace
 * command carries, exactly as the tab has always sent it. A masked team answers with an empty
 * roster rather than guessing at one the viewer may not see.
 */
export function authoritativeProposalClientTeamRoster(
  clientTeam: (ClientTeamJSON & { isMasked?: boolean }) | null | undefined,
): ProposalClientTeamMemberReference[] {
  if (!clientTeam || clientTeam.isMasked) return [];
  const leaderUserId = clientTeam.leader?.userId;
  return normalizeProposalClientTeamRoster(
    (clientTeam.members ?? []).filter((member) => member.userId !== leaderUserId),
  );
}

/**
 * The same roster every time: one entry per membership, ordered by its key.
 *
 * Roster equality is set equality — the command carries a membership list, not a sequence — so
 * comparing two rosters means comparing their normal forms rather than their arrays.
 */
export function normalizeProposalClientTeamRoster(
  members: readonly ProposalClientTeamMemberReference[],
): ProposalClientTeamMemberReference[] {
  const byKey = new Map<string, ProposalClientTeamMemberReference>();
  for (const member of members) {
    if (!member?.userId || !member?.legalEntityId) continue;
    byKey.set(proposalClientTeamMemberKey(member), {
      userId: member.userId,
      legalEntityId: member.legalEntityId,
    });
  }
  return [...byKey.entries()].sort(([left], [right]) => left.localeCompare(right)).map(([, member]) => member);
}

export function proposalClientTeamRostersAreEqual(
  left: readonly ProposalClientTeamMemberReference[],
  right: readonly ProposalClientTeamMemberReference[],
): boolean {
  const normalizedLeft = normalizeProposalClientTeamRoster(left);
  const normalizedRight = normalizeProposalClientTeamRoster(right);
  if (normalizedLeft.length !== normalizedRight.length) return false;
  return normalizedLeft.every(
    (member, index) => proposalClientTeamMemberKey(member) === proposalClientTeamMemberKey(normalizedRight[index]),
  );
}

/**
 * The staged roster, re-expressed against the roster the server holds now.
 *
 * The viewer's edit is a set of additions and removals, not a snapshot: three-way, with the draft's
 * `baseline` as the merge base. A member somebody else added while this edit was unsaved stays on
 * the roster; a member this viewer removed stays removed; a member this viewer added stays added
 * even if the authoritative roster has moved on. Without this, pressing Save would replace the
 * roster with a list assembled before the other change existed and quietly revoke that person's
 * access.
 *
 * The same shape the content queue already has, where pending operations are merged onto the
 * envelope's projection rather than overwriting it.
 */
export function rebaseProposalClientTeamRoster(
  draft: ProposalClientTeamRosterDraft,
  authoritative: readonly ProposalClientTeamMemberReference[],
): ProposalClientTeamMemberReference[] {
  const baselineKeys = new Set(draft.baseline.map(proposalClientTeamMemberKey));
  const stagedKeys = new Set(draft.members.map(proposalClientTeamMemberKey));
  const removedKeys = new Set([...baselineKeys].filter((key) => !stagedKeys.has(key)));
  const added = draft.members.filter((member) => !baselineKeys.has(proposalClientTeamMemberKey(member)));

  const kept = authoritative.filter((member) => !removedKeys.has(proposalClientTeamMemberKey(member)));
  return normalizeProposalClientTeamRoster([...kept, ...added]);
}

/** Whether the staged roster, once rebased, still asks the server for something it does not have. */
export function proposalClientTeamRosterIsDirty(
  draft: ProposalClientTeamRosterDraft | undefined,
  authoritative: readonly ProposalClientTeamMemberReference[],
): boolean {
  if (!draft) return false;
  return !proposalClientTeamRostersAreEqual(rebaseProposalClientTeamRoster(draft, authoritative), authoritative);
}

/**
 * Whether the authoritative roster has moved since this edit began — a teammate's add or removal
 * landing under an unsaved edit of this viewer's own.
 *
 * The edit is kept and rebased rather than dropped, so this is a thing to say, not a thing to
 * recover from: the tab tells the reader their roster is no longer the one they started from.
 */
export function proposalClientTeamRosterMovedUnderEdit(
  draft: ProposalClientTeamRosterDraft | undefined,
  authoritative: readonly ProposalClientTeamMemberReference[],
): boolean {
  if (!draft) return false;
  return !proposalClientTeamRostersAreEqual(draft.baseline, authoritative);
}
